import { clientLinkSchema } from '@fos/shared/contracts/crm';
import type { ClientDocumentsDto } from '@fos/shared/contracts/knowledge';
import { ExternalLink, Link2, Pencil, Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../../lib/api';
import { trpc } from '../../../../lib/trpc';
import { Button, IconButton } from '../../../../ui/Button';
import { Dialog } from '../../../../ui/Dialog';
import { Field } from '../../../../ui/Field';
import { Input } from '../../../../ui/Input';
import { SectionHeader } from '../../../../ui/PageHeader';
import { Section } from '../../../../ui/Section';
import { EmptyState } from '../../../../ui/States';
import { toast } from '../../../../ui/Toast';
import { TOUCH_BUTTON } from '../../../mobile';
import { isPlausibleUrl, linkDomain, normalizeUrl } from './documents-format';

type LinkItem = { label: string; url: string };
type LinkErrors = { label?: string; url?: string };

const URL_ERROR = 'Podaj poprawny adres, np. https://figma.com/…';
type Links = NonNullable<ClientDocumentsDto['links']>;

/** Klucze wierszy: nazwa + adres (+ numer powtórzenia, gdy ten sam link jest dwa razy). */
function linkKeys(items: readonly LinkItem[]): string[] {
  const seen = new Map<string, number>();
  return items.map((l) => {
    const base = `${l.label}|${l.url}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n === 0 ? base : `${base}|${n}`;
  });
}

/** Edycja linku w miejscu wiersza: nazwa i adres (bez protokołu dopisujemy https://). */
function LinkEditor({
  initial,
  saving,
  serverErrors,
  onSave,
  onCancel,
  onEdit,
}: {
  initial: LinkItem;
  saving: boolean;
  /** błędy pól z odpowiedzi serwera (VALIDATION_ERROR, `links.N.url`) */
  serverErrors?: LinkErrors | null;
  onSave: (link: LinkItem) => void;
  onCancel: () => void;
  /** zmiana pola — zdejmuje błędy serwera */
  onEdit?: () => void;
}) {
  const [label, setLabel] = useState(initial.label);
  const [url, setUrl] = useState(initial.url);
  const [errors, setErrors] = useState<LinkErrors>({});

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const candidate = { label: label.trim(), url: normalizeUrl(url) };
    const parsed = clientLinkSchema.safeParse(candidate);
    const next: LinkErrors = {};
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        if (issue.path[0] === 'label') next.label = candidate.label ? 'Najwyżej 100 znaków' : 'Podaj nazwę';
        if (issue.path[0] === 'url') next.url = URL_ERROR;
      }
    }
    if (!next.url && candidate.url && !isPlausibleUrl(candidate.url)) next.url = URL_ERROR;
    if (!parsed.success || next.url || next.label) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSave(parsed.data);
  };

  return (
    <form
      onSubmit={submit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        }
      }}
      aria-label="Edycja linku"
      className="flex flex-col gap-3 border-b border-line py-3 md:flex-row md:items-start"
    >
      <Field label="Nazwa" error={errors.label ?? serverErrors?.label} className="md:w-56">
        <Input
          value={label}
          autoFocus
          maxLength={100}
          placeholder="np. Figma, Dysk, Panel"
          onChange={(e) => {
            setLabel(e.target.value);
            onEdit?.();
          }}
        />
      </Field>
      <Field label="Adres" error={errors.url ?? serverErrors?.url} className="min-w-0 flex-1">
        <Input
          value={url}
          inputMode="url"
          placeholder="https://"
          onChange={(e) => {
            setUrl(e.target.value);
            onEdit?.();
          }}
        />
      </Field>
      <div className="flex gap-2 md:pt-6">
        <Button variant="ghost" onClick={onCancel} disabled={saving} className={TOUCH_BUTTON}>
          Anuluj
        </Button>
        <Button variant="secondary" type="submit" loading={saving} className={TOUCH_BUTTON}>
          Zapisz
        </Button>
      </div>
    </form>
  );
}

/**
 * „Linki” w dokumentach klienta: dotychczasowe `clients.links` (panel, dysk, Figma) — nazwa, domena, otwarcie w nowej
 * karcie; edycja, dodanie i usunięcie w miejscu (zapis `clients.update` z wersją klienta — zmiana w międzyczasie =
 * komunikat o konflikcie).
 */
export function DocumentLinksSection({ clientId, links }: { clientId: string; links: Links }) {
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [toRemove, setToRemove] = useState<number | null>(null);
  const update = trpc.clients.update.useMutation();
  const [serverErrors, setServerErrors] = useState<LinkErrors | null>(null);

  /** `index` = edytowany wiersz (nowy: na końcu listy) — błąd serwera dla jego pól trafia pod pole, nie do toastu */
  const save = async (next: LinkItem[], done: string, index: number | null = null) => {
    setServerErrors(null);
    try {
      await update.mutateAsync({ id: clientId, version: links.version, links: next });
      toast.success(done);
      setEditing(null);
      setToRemove(null);
      await Promise.all([
        utils.clients.documents.list.invalidate({ clientId }),
        utils.clients.get.invalidate({ id: clientId }),
      ]);
    } catch (err) {
      const fields: LinkErrors = {};
      if (index !== null && appErrorCode(err) === 'VALIDATION_ERROR') {
        for (const issue of validationIssues(err)) {
          if (issue.path === `links.${index}.url`) fields.url = URL_ERROR;
          if (issue.path === `links.${index}.label`) fields.label = issue.message;
        }
      }
      if (fields.url || fields.label) {
        setServerErrors(fields);
        return;
      }
      toast.error('Nie udało się zapisać linków', {
        description:
          appErrorCode(err) === 'CONFLICT'
            ? 'Ktoś właśnie zmienił dane klienta. Odśwież stronę i spróbuj ponownie.'
            : errorMessage(err),
      });
    }
  };

  const items = links.items;
  const keys = linkKeys(items);
  const removing = toRemove !== null ? items[toRemove] : undefined;
  return (
    <Section aria-labelledby="client-documents-links">
      <SectionHeader
        title={<span id="client-documents-links">Linki</span>}
        action={
          links.canEdit && editing === null && items.length > 0 ? (
            <Button variant="ghost" size="s" icon={Plus} onClick={() => setEditing('new')} className={TOUCH_BUTTON}>
              Dodaj link
            </Button>
          ) : undefined
        }
      />
      {items.length === 0 && editing !== 'new' ? (
        <EmptyState
          compact
          icon={Link2}
          title="Brak linków"
          description="Panel klienta, dysk z materiałami, Figma — wszystko pod ręką."
          action={
            links.canEdit ? (
              <Button
                variant="secondary"
                size="s"
                icon={Plus}
                onClick={() => setEditing('new')}
                className={TOUCH_BUTTON}
              >
                Dodaj link
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul aria-label="Linki klienta">
          {items.map((link, i) =>
            editing === i ? (
              <li key={keys[i]}>
                <LinkEditor
                  initial={link}
                  saving={update.isPending}
                  serverErrors={serverErrors}
                  onEdit={() => setServerErrors(null)}
                  onCancel={() => {
                    setServerErrors(null);
                    setEditing(null);
                  }}
                  onSave={(l) =>
                    void save(
                      items.map((x, j) => (j === i ? l : x)),
                      'Link zapisany',
                      i,
                    )
                  }
                />
              </li>
            ) : (
              <li key={keys[i]} className="flex items-center gap-3 border-b border-line py-3">
                <span
                  aria-hidden
                  className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-surface text-fg-secondary"
                >
                  <Link2 size={16} strokeWidth={1.75} />
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-w-0 items-center gap-1 rounded-xs text-body-s font-medium text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
                  >
                    <span className="truncate">{link.label}</span>
                    <ExternalLink size={12} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
                  </a>
                  <span className="truncate text-caption text-fg-muted">{linkDomain(link.url)}</span>
                </div>
                {links.canEdit && editing === null && (
                  <div className="flex shrink-0 items-center gap-1">
                    <IconButton
                      icon={Pencil}
                      label={`Edytuj link ${link.label}`}
                      size={32}
                      iconSize={16}
                      onClick={() => setEditing(i)}
                      className="max-md:size-11"
                    />
                    <IconButton
                      icon={Trash2}
                      label={`Usuń link ${link.label}`}
                      size={32}
                      iconSize={16}
                      onClick={() => setToRemove(i)}
                      className="max-md:size-11"
                    />
                  </div>
                )}
              </li>
            ),
          )}
          {editing === 'new' && (
            <li>
              <LinkEditor
                initial={{ label: '', url: '' }}
                saving={update.isPending}
                serverErrors={serverErrors}
                onEdit={() => setServerErrors(null)}
                onCancel={() => {
                  setServerErrors(null);
                  setEditing(null);
                }}
                onSave={(l) => void save([...items, l], 'Link dodany', items.length)}
              />
            </li>
          )}
        </ul>
      )}
      <Dialog
        open={removing !== undefined}
        onOpenChange={(next) => !next && setToRemove(null)}
        width="s"
        title="Usunąć link?"
        description={
          removing ? `„${removing.label}” (${linkDomain(removing.url)}) zniknie z karty klienta.` : undefined
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setToRemove(null)} className={TOUCH_BUTTON}>
              Anuluj
            </Button>
            <Button
              variant="primary"
              loading={update.isPending}
              onClick={() =>
                toRemove !== null &&
                void save(
                  items.filter((_, j) => j !== toRemove),
                  'Link usunięty',
                )
              }
              className={TOUCH_BUTTON}
            >
              Usuń link
            </Button>
          </>
        }
      />
    </Section>
  );
}
